import { formatDate, getWeekday } from '@/lib/date';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';
import type { LessonRow, GroupInfo } from './WeekCalendar';

interface LessonListProps {
  lessons: LessonRow[];
  groupMap: Record<string, GroupInfo>;
  onSelectLesson: (lesson: LessonRow) => void;
}

export default function LessonList({ lessons, groupMap, onSelectLesson }: LessonListProps) {
  if (lessons.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-background-300 bg-white p-12 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-calendar-2-line text-2xl" />
        </div>
        <p className="mt-4 text-sm font-semibold text-foreground-800">Bu filtreyle eşleşen ders bulunamadı</p>
        <p className="mt-1 text-sm text-foreground-500">Farklı bir filtre deneyin veya yeni bir ders planlayın.</p>
      </div>
    );
  }

  // Tarihe göre grupla
  const groupsByDate: Record<string, LessonRow[]> = {};
  lessons.forEach((l) => {
    const key = l.date ?? 'belirsiz';
    if (!groupsByDate[key]) groupsByDate[key] = [];
    groupsByDate[key].push(l);
  });

  const sortedDates = Object.keys(groupsByDate).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-6">
      {sortedDates.map((dateKey) => {
        const dayLessons = groupsByDate[dateKey].slice().sort((a, b) =>
          (a.start_time ?? '').localeCompare(b.start_time ?? ''),
        );
        const heading = dateKey === 'belirsiz' ? 'Tarihsiz Dersler' : formatDate(dateKey);

        return (
          <div key={dateKey}>
            <div className="mb-3 flex items-center gap-2">
              <h3 className="font-heading text-base font-bold text-foreground-950">{heading}</h3>
              {dateKey !== 'belirsiz' && (
                <span className="text-sm capitalize text-foreground-500">{getWeekday(dateKey)}</span>
              )}
              <span className="ml-1 rounded-full bg-background-200/70 px-2 py-0.5 text-xs font-semibold text-foreground-600">
                {dayLessons.length} ders
              </span>
            </div>

            <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
              {dayLessons.map((lesson, idx) => {
                const group = lesson.group_id ? groupMap[lesson.group_id] : undefined;
                const status = getStatusMeta(lesson.status, lessonStatusMap);
                return (
                  <div
                    key={lesson.id}
                    className={`flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between ${
                      idx !== dayLessons.length - 1 ? 'border-b border-background-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3.5">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white"
                        style={{ backgroundColor: group?.color ?? '#94a3b8' }}
                      >
                        <i className={`${lesson.type === 'birebir' ? 'ri-user-line' : 'ri-team-line'} text-lg`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-heading text-sm font-bold text-foreground-950">{lesson.title}</p>
                          {lesson.live_link && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                              <i className="ri-live-line" /> Canlı
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-foreground-500">
                          {lesson.start_time ?? '--:--'} - {lesson.end_time ?? '--:--'}
                          {group ? ` · ${group.name}` : ''}
                          {lesson.level ? ` · ${lesson.level}` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <StatusBadge meta={status} />
                      <button
                        type="button"
                        onClick={() => onSelectLesson(lesson)}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-eye-line" /> Detay
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}